import classNames from "classnames";
import type { DropImageHandler, ImageSource } from "common/components/portable-renderers";
import { canMakeUrl, imageSourceUrl, MapTheme } from "common/components/portable-renderers";
import type { AppEnvironment } from "common/components/types";
import type { DropData } from "common/drop-controller";
import { ImageAspectRatio } from "common/generator/components/image-types";
import { TitleAppearance } from "common/generator/components/title-style";
import { defined } from "common/Support";
import { AutoSizer } from "react-virtualized/dist/es/AutoSizer";
import { withTheme } from "styled-components";
import type { RuntimeTheme } from "../../../lib/theme/theme";
import TextSummaryView from "../../views/text-summary/text-summary-view";
import Droppable from "../droppable/droppable";
import UserProfile, { UserProfileImageSize } from "../user-profile/user-profile";
import {
    Image,
    ImageStyle,
    ImageSummaryViewDetailsStyle,
    Skeleton,
    TitleViewSubtitleStyle,
    TitleViewTitleStyle,
    Wrapper,
} from "./title-style";

interface Props {
    readonly appEnvironment: AppEnvironment;
    readonly title?: string;
    readonly subtitle?: string;
    readonly imageSource?: ImageSource;
    readonly onClick?: React.MouseEventHandler<HTMLDivElement>;
    readonly onDropImage?: DropImageHandler;
    readonly theme: RuntimeTheme;
    readonly appearance: TitleAppearance;
    readonly aspect?: ImageAspectRatio;
}

interface State {
    dragOver: boolean;
    tmpSrc?: string;
    url: string;
}

export const TitleSkeleton = () => {
    return <Skeleton />;
};

class TitleView extends Droppable<Props, State> {
    public state: State = { dragOver: false, url: "" };

    private async updateUrl(imageSource: ImageSource, width: number, height: number, zoom: number) {
        const theme = this.props.theme.mapTheme === "light" ? MapTheme.Light : MapTheme.Dark;
        const url = await imageSourceUrl(this.props.appEnvironment, imageSource, width, height, zoom, theme);
        if (url !== undefined && url !== this.state.url) {
            this.setState({ url });
        }
    }

    protected onDataDropped(dropData: DropData): void {
        switch (dropData.kind) {
            case "uri":
                this.setState({ tmpSrc: dropData.uri });
                break;
            case "file":
                const fr = new FileReader();
                fr.onload = (_e: Event) => {
                    const newUrl = fr.result as string;
                    this.setState({ tmpSrc: newUrl });
                };

                fr.readAsDataURL(dropData.file);
                break;
        }

        super.onDataDropped(dropData);
    }

    public componentWillReceiveProps(nextProps: Props) {
        if (this.props.imageSource === nextProps.imageSource) return;
        this.setState({ tmpSrc: undefined });
    }

    public render(): React.ReactNode {
        const { appearance } = this.props;

        let content;
        switch (appearance) {
            case TitleAppearance.Filled:
                content = this.renderFullStyle();
                break;
            case TitleAppearance.Card:
                content = this.renderFullStyle({ cardStyle: true });
                break;
            case TitleAppearance.Profile:
                content = this.renderProfileStyle();
                break;
        }

        return (
            <div data-test="app-summary" {...this.getDroppableProps()} style={this.getDroppableStyle()}>
                {content}
            </div>
        );
    }

    public renderProfileStyle(): React.ReactNode {
        const { imageSource, onClick } = this.props;
        const { tmpSrc } = this.state;

        if (canMakeUrl(imageSource)) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this.updateUrl(defined(imageSource), UserProfileImageSize, UserProfileImageSize, 15);
        }

        return (
            <UserProfile
                canUploadImage={false}
                {...this.props}
                imageURL={tmpSrc === undefined ? this.state.url : tmpSrc}
                onClick={onClick}
            />
        );
    }

    public renderFullStyle(additionalProps: { cardStyle?: boolean } = {}): React.ReactNode {
        const { imageSource, onClick, title, subtitle, aspect } = this.props;
        const { cardStyle = false } = additionalProps;

        const { tmpSrc } = this.state;

        if (!canMakeUrl(imageSource) && tmpSrc === undefined) {
            return <TextSummaryView title={title} subtitle={subtitle} onClick={onClick} />;
        }

        let details: React.ReactNode;
        if (title !== undefined || subtitle !== undefined) {
            details = (
                <ImageSummaryViewDetailsStyle>
                    <TitleViewTitleStyle data-test="app-summary-title">{title}</TitleViewTitleStyle>
                    <TitleViewSubtitleStyle data-test="app-summary-subtitle">{subtitle}</TitleViewSubtitleStyle>
                </ImageSummaryViewDetailsStyle>
            );
        }

        return (
            <Wrapper
                cardStyle={cardStyle}
                className={classNames(
                    aspect === ImageAspectRatio.ThreeByOne && "threeby1",
                    aspect === ImageAspectRatio.ThreeByTwo && "threeby2"
                )}>
                <div className="title-wrapper">
                    <AutoSizer>
                        {(props: { width: number; height: number }) => {
                            if (canMakeUrl(imageSource)) {
                                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                                this.updateUrl(defined(imageSource), props.width, props.height, 15);
                            }
                            return (
                                <>
                                    <ImageStyle src={tmpSrc === undefined ? this.state.url : tmpSrc} />
                                    <Image onClick={onClick}>{details}</Image>
                                </>
                            );
                        }}
                    </AutoSizer>
                </div>
            </Wrapper>
        );
    }
}

export default withTheme(TitleView);
